// (2026-06-09) MAESTRO de Compras — LISTA como PAGINA (estilo ListInvoices).
//
// Reemplaza el flujo "todo-en-un-modal" de ComprasPage.js por el patron del
// maestro de Facturas: la lista es una pagina y el crear/editar viven en una
// pagina-formulario (CompraForm.js) con rutas /compras/facturas/create y
// /compras/facturas/edit?id=<cifrado>.
//
// Conserva EXACTAMENTE la logica de negocio y las llamadas API del modulo:
//   - getCompras() para poblar la tabla
//   - getCompra(id) para el detalle (modal de visualizacion)
//   - registerCompra(id, body) — accion "Registrar" (prompt ISLR + confirm)
//   - addCompraPago(id, body)  — accion "Pagar" (modal de pago)
//   - cancelCompra(id, motivo) — accion "Anular" (prompt motivo)
//   - getRetentionVouchers(id) — ver comprobantes de retencion
//   - getProveedores / getMetodosPago para los catalogos auxiliares
// Las acciones dinamicas por estatus y las validaciones son las mismas que hoy.
//
// Editar SOLO esta disponible en estatus 'borrador'; navega a la pagina
// formulario con el id cifrado (encryptText), igual que el maestro.
//
// Reusa los primitivos de ./ui (NADA de @mui) y la guia _DESIGN_GUIDE.md.

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import AdminLayout from "../../layouts/AdminLayout";
import { encryptText } from "../../controllers/api";
import {
  getCompras, getCompra, registerCompra, addCompraPago, cancelCompra,
  getRetentionVouchers, getProveedores, getMetodosPago,
} from "../../controllers/api_compras";
import { MONEDAS, monedaCodigo, fmt, handleApiError, hoyISO } from "./comprasShared";
import {
  Modal, Card, PageHeader, Field, TextInput, SelectInput,
  BtnPrimary, BtnGhost, IconBtn, SectionTitle, Table, Th, Td,
} from "./ui";

// Mapa de estatus -> clases de badge (semantica de la guia de diseno).
const ESTATUS_BADGE = {
  borrador: "bg-slate-100 text-slate-700",
  registrada: "bg-twilight-indigo-100 text-twilight-indigo-800",
  pagada_parcial: "bg-amber-100 text-amber-800",
  pagada_total: "bg-emerald-400 text-white",
  anulada: "bg-red-400 text-white",
};
const estatusClases = (s) => ESTATUS_BADGE[s] || "bg-slate-100 text-slate-700";

const TIPO_DOC_LABEL = { FC: "Factura", NC: "Nota de Crédito", ND: "Nota de Débito", IMP: "Importación" };

export default function ComprasList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [metodos, setMetodos] = useState([]);

  // Modal de detalle (visualizacion).
  const [detalle, setDetalle] = useState(null);     // compra cargada con getCompra
  const [detalleVouchers, setDetalleVouchers] = useState([]);

  // Modal de pago (accion desde la lista, como hoy).
  const [pago, setPago] = useState(null);           // compra a pagar
  const [pagoForm, setPagoForm] = useState({ metodo_pago_id: "", monto: "", moneda_id: 3, banco: "", referencia: "", fecha_pago: hoyISO() });
  const [pagoErrors, setPagoErrors] = useState({});

  const cargar = async () => { try { setRows(await getCompras()); } catch (e) { handleApiError(e); } };
  useEffect(() => {
    cargar();
    getProveedores({ activo: 1 }).catch(() => {});
    getMetodosPago().then((d) => setMetodos(Array.isArray(d) ? d : (d?.data || []))).catch(() => {});
  }, []);

  // ----- Navegacion crear/editar (pagina-formulario) -----
  const irACrear = () => navigate("/compras/facturas/create");
  const irAEditar = (r) => {
    const hash = encryptText(r.id.toString());
    navigate(`/compras/facturas/edit?id=${encodeURIComponent(hash)}`);
  };

  // ----- Detalle (modal) -----
  const verDetalle = async (r) => {
    try {
      const c = await getCompra(r.id);
      setDetalle(c);
      setDetalleVouchers([]);
      try { setDetalleVouchers(await getRetentionVouchers(r.id) || []); } catch (_) { /* sin comprobantes */ }
    } catch (e) { handleApiError(e, "No se pudo cargar el detalle."); }
  };

  // ----- Acciones de negocio (identicas a ComprasPage.js) -----
  const registrar = async (r) => {
    let body = {};
    if (r.aplica_retencion_islr) {
      const pct = window.prompt("Porcentaje de retención ISLR (%):", "3");
      if (pct === null) return;
      if (String(pct).trim() === "" || isNaN(Number(pct)) || Number(pct) < 0) {
        toast.error("- El porcentaje de retención ISLR debe ser un número válido");
        return;
      }
      body.retencion_islr_porcentaje = pct;
    }
    if (!window.confirm("¿Registrar la compra? Se calcularán retenciones y se emitirán comprobantes.")) return;
    try { await registerCompra(r.id, body); toast.success("Compra registrada."); cargar(); } catch (e) { handleApiError(e); }
  };

  const abrirPago = (r) => {
    setPago(r);
    setPagoForm({ metodo_pago_id: "", monto: "", moneda_id: r.moneda_id, banco: "", referencia: "", fecha_pago: hoyISO() });
    setPagoErrors({});
  };

  // Validación del modal de pago (patrón canónico: validate() de ProveedoresForm).
  // Banco y referencia son OPCIONALES (pagos en efectivo no los requieren).
  const validatePago = () => {
    const newErrors = {};
    const errorToast = [];
    if (!pagoForm.metodo_pago_id) {
      newErrors.metodo_pago_id = "Método de pago es obligatorio";
      errorToast.push("- Método de pago es obligatorio");
    }
    if (
      pagoForm.monto === "" || pagoForm.monto === null || pagoForm.monto === undefined ||
      isNaN(Number(pagoForm.monto)) || Number(pagoForm.monto) <= 0
    ) {
      newErrors.monto = "Monto debe ser un número mayor que 0";
      errorToast.push("- Monto debe ser un número mayor que 0");
    }
    if (!pagoForm.moneda_id) {
      newErrors.moneda_id = "Moneda es obligatoria";
      errorToast.push("- Moneda es obligatoria");
    }
    if (!pagoForm.fecha_pago) {
      newErrors.fecha_pago = "Fecha de pago es obligatoria";
      errorToast.push("- Fecha de pago es obligatoria");
    }
    setPagoErrors(newErrors);
    if (errorToast.length > 0) {
      toast.error(
        <div>
          {errorToast.map((item, i) => (
            <span key={i} className="text-start">{item}<br /></span>
          ))}
        </div>
      );
    }
    return Object.keys(newErrors).length === 0;
  };

  const guardarPago = async () => {
    if (!validatePago()) return;
    try { await addCompraPago(pago.id, pagoForm); toast.success("Pago agregado."); setPago(null); cargar(); } catch (e) { handleApiError(e); }
  };

  const anular = async (r) => {
    const motivo = window.prompt("Motivo de anulación:", "Anulada");
    if (motivo === null) return;
    const motivoLimpio = String(motivo).toUpperCase().trim();
    if (!motivoLimpio) { toast.error("- El motivo de anulación es obligatorio"); return; }
    try { await cancelCompra(r.id, motivoLimpio); toast.success("Compra anulada."); cargar(); } catch (e) { handleApiError(e); }
  };

  const verComprobantes = async (r) => {
    try {
      const vs = await getRetentionVouchers(r.id);
      if (!vs || !vs.length) { toast.info("Esta compra no tiene comprobantes de retención."); return; }
      // Se muestran dentro del modal de detalle para no inventar otra pantalla.
      const c = await getCompra(r.id);
      setDetalle(c);
      setDetalleVouchers(vs);
    } catch (e) { handleApiError(e, "No se pudieron cargar los comprobantes."); }
  };

  // Helper de seccion del detalle (estilo InvoiceDetailView).
  const DetItem = ({ label, children }) => (
    <div className="w-full sm:w-1/2 lg:w-1/3 px-2 mb-3">
      <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-3 h-full">
        <h4 className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-500 mb-1">{label}</h4>
        <div className="text-sm font-medium text-ink-900 break-words">{children ?? "—"}</div>
      </div>
    </div>
  );

  const d = detalle;
  const codMon = d ? monedaCodigo(d.moneda_id) : "";

  return (
    <AdminLayout nombreUsuario={user?.nombre || "Usuario"}>
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="mt-8 px-4 md:px-8">
        <Card>
          <PageHeader
            title="Compras"
            subtitle="Facturas de proveedor, registro, pagos y anulaciones"
            icon="fa-cart-shopping"
            actions={
              <button
                className="bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-bold py-2 px-4 rounded inline-flex items-center gap-2"
                onClick={irACrear}
              >
                <i className="fas fa-plus" aria-hidden="true" />
                Nueva compra
              </button>
            }
          />

          {/* TABLA (columnas estilo lista de facturas) */}
          <Table>
            <thead>
              <tr>
                <Th>Fecha</Th>
                <Th>Proveedor</Th>
                <Th>Nº Factura</Th>
                <Th>Nº Control</Th>
                <Th>Tipo</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-center">Mon.</Th>
                <Th className="text-center">Estatus</Th>
                <Th className="text-center">Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <Td className="text-center text-blueGray-400 italic" colSpan={9}>Sin compras.</Td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-blueGray-50 transition-colors">
                  <Td>{(r.fecha_emision || "").slice(0, 10)}</Td>
                  <Td>{r.proveedor_nombre}</Td>
                  <Td>{r.numero_factura_proveedor}</Td>
                  <Td>{r.numero_control_proveedor}</Td>
                  <Td>{TIPO_DOC_LABEL[r.tipo_documento] || r.tipo_documento}</Td>
                  <Td className="text-right font-semibold">{fmt(r.total_neto)}</Td>
                  <Td className="text-center">{monedaCodigo(r.moneda_id)}</Td>
                  <Td className="text-center">
                    <span className={`inline-block px-2 py-1 text-xs font-bold rounded ${estatusClases(r.estatus)}`}>
                      {r.estatus}
                    </span>
                  </Td>
                  <Td>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.25rem", whiteSpace: "nowrap" }}>
                      {/* Ver detalle */}
                      <IconBtn icon="fa-expand" title="Ver" color="text-gray-700 hover:text-gray-900" onClick={() => verDetalle(r)} />
                      {/* Editar (solo borrador) */}
                      {r.estatus === "borrador" && (
                        <IconBtn icon="fa-pen-to-square" title="Editar" color="text-blue-600 hover:text-blue-800" onClick={() => irAEditar(r)} />
                      )}
                      {/* Registrar (borrador) */}
                      {r.estatus === "borrador" && (
                        <IconBtn icon="fa-circle-check" title="Registrar" color="text-twilight-indigo-600 hover:text-twilight-indigo-800" onClick={() => registrar(r)} />
                      )}
                      {/* Pagar (registrada / pagada_parcial) */}
                      {["registrada", "pagada_parcial"].includes(r.estatus) && (
                        <IconBtn icon="fa-money-bill-wave" title="Pagar" color="text-emerald-600 hover:text-emerald-800" onClick={() => abrirPago(r)} />
                      )}
                      {/* Comprobantes de retencion (si retiene IVA o ISLR) */}
                      {(r.aplica_retencion_iva || r.aplica_retencion_islr) && r.estatus !== "borrador" && (
                        <IconBtn icon="fa-file-shield" title="Comprobantes de retención" color="text-amber-600 hover:text-amber-800" onClick={() => verComprobantes(r)} />
                      )}
                      {/* Anular */}
                      {r.estatus !== "anulada" && (
                        <IconBtn icon="fa-ban" title="Anular" color="text-red-600 hover:text-red-800" onClick={() => anular(r)} />
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        {/* ===================== MODAL: Detalle de la compra ===================== */}
        <Modal
          open={!!detalle}
          onClose={() => { setDetalle(null); setDetalleVouchers([]); }}
          title={`Detalle de la ${d ? (TIPO_DOC_LABEL[d.tipo_documento] || "compra") : "compra"}`}
          icon="fa-file-invoice-dollar"
        >
          {d && (
            <>
              <SectionTitle icon="fa-circle-info">Documento</SectionTitle>
              <div className="flex flex-wrap -mx-2">
                <DetItem label="Proveedor">{d.proveedor_nombre}</DetItem>
                <DetItem label="RIF proveedor">{d.proveedor_rif}</DetItem>
                <DetItem label="Tipo documento">{TIPO_DOC_LABEL[d.tipo_documento] || d.tipo_documento}</DetItem>
                <DetItem label="Nº Factura proveedor">{d.numero_factura_proveedor}</DetItem>
                <DetItem label="Nº Control proveedor">{d.numero_control_proveedor}</DetItem>
                <DetItem label="Fecha emisión">{(d.fecha_emision || "").slice(0, 10)}</DetItem>
                <DetItem label="Moneda">{codMon}</DetItem>
                <DetItem label="Estatus">
                  <span className={`inline-block px-2 py-1 text-xs font-bold rounded ${estatusClases(d.estatus)}`}>{d.estatus}</span>
                </DetItem>
                <DetItem label="Período fiscal">{d.periodo_fiscal}</DetItem>
              </div>

              <SectionTitle icon="fa-list">Ítems</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <Th>Descripción</Th>
                    <Th className="text-right">Cant.</Th>
                    <Th className="text-right">Precio unit.</Th>
                    <Th className="text-center">IVA</Th>
                    <Th className="text-right">Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {(d.items || []).length === 0 && (
                    <tr><Td className="text-center text-blueGray-400 italic" colSpan={5}>Sin ítems.</Td></tr>
                  )}
                  {(d.items || []).map((it) => (
                    <tr key={it.id}>
                      <Td>{it.descripcion}</Td>
                      <Td className="text-right">{fmt(it.cantidad)}</Td>
                      <Td className="text-right">{fmt(it.precio_unitario)}</Td>
                      <Td className="text-center">
                        {it.aplica_iva
                          ? `${(it.iva_categoria && it.iva_categoria.tasa_porcentaje) ?? ""}%`
                          : "Exento"}
                      </Td>
                      <Td className="text-right">{fmt(it.total)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <SectionTitle icon="fa-coins">Totales</SectionTitle>
              <div className="flex flex-wrap -mx-2">
                <DetItem label="Base imponible">{fmt(d.total_base)} {codMon}</DetItem>
                <DetItem label="I.V.A.">{fmt(d.total_impuestos)} {codMon}</DetItem>
                <DetItem label="Total">{fmt(d.total_neto)} {codMon}</DetItem>
                {Number(d.retencion_iva_monto) > 0 && (
                  <DetItem label="Retención IVA">{fmt(d.retencion_iva_monto)} {codMon}</DetItem>
                )}
                {Number(d.retencion_islr_monto) > 0 && (
                  <DetItem label="Retención ISLR">{fmt(d.retencion_islr_monto)} {codMon}</DetItem>
                )}
                {Number(d.igtf_monto) > 0 && (
                  <DetItem label="IGTF">{fmt(d.igtf_monto)} {codMon}</DetItem>
                )}
              </div>

              {(d.pagos || []).length > 0 && (
                <>
                  <SectionTitle icon="fa-money-bill-wave">Pagos</SectionTitle>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Fecha</Th>
                        <Th>Método</Th>
                        <Th>Banco</Th>
                        <Th>Referencia</Th>
                        <Th className="text-right">Monto</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.pagos.map((p) => (
                        <tr key={p.id}>
                          <Td>{(p.fecha_pago || "").slice(0, 10)}</Td>
                          <Td>{p.metodo_pago_nombre || p.metodo_pago_codigo}</Td>
                          <Td>{p.banco || "—"}</Td>
                          <Td>{p.referencia || "—"}</Td>
                          <Td className="text-right">{fmt(p.monto)} {monedaCodigo(p.moneda_id)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {detalleVouchers.length > 0 && (
                <>
                  <SectionTitle icon="fa-file-shield">Comprobantes de retención</SectionTitle>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Tipo</Th>
                        <Th>Nº Comprobante</Th>
                        <Th>Período</Th>
                        <Th className="text-right">Base</Th>
                        <Th className="text-right">%</Th>
                        <Th className="text-right">Retenido</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalleVouchers.map((v) => (
                        <tr key={v.id}>
                          <Td>{v.tipo}</Td>
                          <Td>{v.numero_comprobante}</Td>
                          <Td>{v.periodo_fiscal}</Td>
                          <Td className="text-right">{fmt(v.base_imponible)}</Td>
                          <Td className="text-right">{fmt(v.porcentaje_retencion)}%</Td>
                          <Td className="text-right">{fmt(v.monto_retenido)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {d.observaciones && (
                <>
                  <SectionTitle icon="fa-comment">Observaciones</SectionTitle>
                  <p className="px-2 text-sm text-ink-900">{d.observaciones}</p>
                </>
              )}
            </>
          )}
        </Modal>

        {/* ===================== MODAL: Pago de la compra ===================== */}
        <Modal
          open={!!pago}
          onClose={() => setPago(null)}
          title="Pago de la compra"
          icon="fa-money-bill-wave"
          maxWidth="max-w-[640px]"
          footer={
            <>
              <BtnGhost icon="fa-xmark" onClick={() => setPago(null)}>Cancelar</BtnGhost>
              <BtnPrimary icon="fa-check" onClick={guardarPago}>Registrar pago</BtnPrimary>
            </>
          }
        >
          <div className="flex flex-wrap -mx-4">
            <Field label="Método de pago *" className="w-full lg:w-6/12 px-4" error={pagoErrors.metodo_pago_id}>
              <SelectInput
                value={pagoForm.metodo_pago_id}
                onChange={(v) => setPagoForm((f) => ({ ...f, metodo_pago_id: v }))}
                placeholder="Seleccione..."
              >
                {metodos.map((m) => (
                  <option key={m.id} value={m.id}>{m.nombre}{m.aplica_igtf ? " (IGTF)" : ""}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Moneda *" className="w-full lg:w-6/12 px-4" error={pagoErrors.moneda_id}>
              <SelectInput value={pagoForm.moneda_id} onChange={(v) => setPagoForm((f) => ({ ...f, moneda_id: v }))}>
                {MONEDAS.map((m) => <option key={m.id} value={m.id}>{m.codigo}</option>)}
              </SelectInput>
            </Field>
            <Field label="Monto *" className="w-full lg:w-6/12 px-4" error={pagoErrors.monto}>
              <TextInput type="number" value={pagoForm.monto} onChange={(v) => setPagoForm((f) => ({ ...f, monto: v }))} />
            </Field>
            <Field label="Fecha de pago *" className="w-full lg:w-6/12 px-4" error={pagoErrors.fecha_pago}>
              <TextInput type="date" value={pagoForm.fecha_pago} onChange={(v) => setPagoForm((f) => ({ ...f, fecha_pago: v }))} />
            </Field>
            <Field label="Banco" className="w-full lg:w-6/12 px-4">
              <TextInput value={pagoForm.banco} onChange={(v) => setPagoForm((f) => ({ ...f, banco: v }))} />
            </Field>
            <Field label="Referencia" className="w-full lg:w-6/12 px-4">
              <TextInput value={pagoForm.referencia} onChange={(v) => setPagoForm((f) => ({ ...f, referencia: v }))} />
            </Field>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
